/**
 * Layout Component
 * 
 * Query Strategy:
 * - useAppBootstrap: Core identity data (user, company, permissions, features, plan)
 *   - Loaded at Layout level for fast initial render
 *   - Shows skeleton while loading
 *   - Data passed down to Header, Sidebar, and other children
 * 
 * - Header uses useHeaderBootstrap and useHeaderCounts for its own data
 *   - Header renders immediately, counts update asynchronously
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useParams } from 'react-router-dom';
import Header from './Header';
import Sidebar from './Sidebar';
import AnnouncementsWall from './AnnouncementsWall';
import BadgeUpgradePopup from './BadgeUpgradePopup';
import StorageFullBanner, { useBannerHeight } from './StorageFullBanner';
import TrialBanner, { useTrialBannerHeight } from './TrialBanner';
import { Card, CardHeader } from '../components/ui/Card';
import { useStorageState } from '../contexts/StorageStateContext';
import { useAppBootstrap } from '../lib/query/bootstrapHooks';
import { getActiveCompanySlug, getCompanySlugFromLocation, clearCompanySlug } from '../utils/companySlug';
import { useAuthIntegrityCheck } from '../hooks/useAuthIntegrityCheck';
import { isSuperadminRoute } from '../utils/routeHelpers';
// useAppBootstrapState and PostLoginLoader removed - no global loading, pages render immediately
import { useBootstrapClientsData } from '../hooks/useBootstrapClientsData';
import { useBootstrapLeadsData } from '../hooks/useBootstrapLeadsData';
import { useBootstrapAgentsData } from '../hooks/useBootstrapAgentsData';
import { useBootstrapHomeData } from '../hooks/useBootstrapHomeData';
import { useBootstrapInventoryData } from '../hooks/useBootstrapInventoryData';
import { usePostLoginBootstrap } from '../hooks/usePostLoginBootstrap';
import { useClient } from '../contexts/ClientContext';
import { useBodyScrollLockAuto } from '../hooks/useBodyScrollLock';

const version = process.env.REACT_APP_VERSION;

/* small hook to track desktop breakpoint */
function useMediaQuery(q: string) {
  const [ok, setOk] = useState(() => typeof window !== 'undefined' ? window.matchMedia(q).matches : false);
  useEffect(() => {
    const m = window.matchMedia(q);
    const cb = () => setOk(m.matches);
    cb();
    m.addEventListener?.('change', cb);
    return () => m.removeEventListener?.('change', cb);
  }, [q]);
  return ok;
}

// Component to handle spacing for header and banner
const BannerSpacer: React.FC<{ isDesktop: boolean }> = ({ isDesktop }) => {
  const { storageState } = useStorageState();
  const headerHeight = isDesktop ? 56 : 48;
  
  // Banner heights - if both are visible, we need to sum them (stacked)
  const storageBannerHeight = storageState && (storageState.readOnly || storageState.almostFull) ? 72 : 0;
  const trialBannerHeight = useTrialBannerHeight();
  // Both banners stack, so we sum them
  const bannerHeight = storageBannerHeight + trialBannerHeight;
  const totalHeight = headerHeight + bannerHeight;

  return <div className="flex-shrink-0" style={{ height: `${totalHeight}px` }} aria-hidden="true" />;
};

const Layout: React.FC = () => {
  const location = useLocation();
  const { company: companyFromParams } = useParams<{ company?: string }>();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isDesktop = useMediaQuery('(min-width: 768px)');

  // CRITICAL: Run auth integrity check FIRST, before any other logic
  // This prevents session mixing between companies
  useAuthIntegrityCheck();

  // Bootstrap state check removed - no global loading blockers

  const toggleSidebar = () => setSidebarOpen(v => !v);
  const closeSidebar = () => setSidebarOpen(false);

  const showChrome = location.pathname !== '/login';

  // CRITICAL: Skip company slug resolution for superadmin routes
  // Superadmin routes do NOT require or use company slug
  const isSuperadmin = isSuperadminRoute(location);
  
  // CRITICAL: Always get company slug from URL first (never from localStorage)
  // Priority: URL > localStorage v2 > user data > env default
  // URL slug is ALWAYS the source of truth when present
  // BUT: Skip this entirely for superadmin routes
  const companySlug = useMemo(() => {
    // CRITICAL: Superadmin routes don't need company slug
    if (isSuperadmin) {
      return null;
    }
    
    // 1) Try URL params first (from React Router)
    if (companyFromParams && companyFromParams !== 'undefined') {
      // Validate basic format
      if (/^[a-zA-Z0-9_-]+$/.test(companyFromParams) && 
          companyFromParams.length >= 1 && 
          companyFromParams.length <= 100) {
        return companyFromParams;
      } else {
        console.warn('[Layout] Invalid company slug from params:', {
          invalid: companyFromParams,
          pathname: location.pathname,
        });
      }
    }
    
    // 2) Extract from URL pathname (URL is always source of truth)
    const slugFromUrl = getCompanySlugFromLocation();
    if (slugFromUrl) {
      return slugFromUrl;
    }
    
    // CRITICAL: If on superadmin route, clear any stale slug and return empty
    if (isSuperadmin) {
      clearCompanySlug();
      return '';
    }
    
    // 3) Use centralized resolver (localStorage v2 > user data > env default)
    // This will return empty string on non-company routes
    return getActiveCompanySlug();
  }, [companyFromParams, location.pathname, isSuperadmin]);

  // Load app bootstrap data (core identity + layout config)
  // This ensures fast initial load - header and layout appear immediately
  // CRITICAL: For superadmin, pass 'superadmin' explicitly (not undefined)
  // The useAppBootstrap hook handles 'superadmin' as a special account type
  const { data: bootstrapData, isLoading: bootstrapLoading, error: bootstrapError } = useAppBootstrap(
    isSuperadmin ? 'superadmin' : (companySlug || undefined),
    {
      // Only fetch if we have a valid route (superadmin or company) and not on login page
      enabled: (isSuperadmin || (!!companySlug && companySlug !== 'undefined')) && showChrome,
    }
  );

  // Get client context to check authentication
  const { client } = useClient();

  // Bootstrap prefetch: Prefetch Clients data as soon as authenticated app mounts
  // This ensures Clients page appears instantly when user clicks on it
  // Runs in Layout (not just Home) so it starts immediately after login
  // Skip clients bootstrap for vendor (no access to clients list → 403)
  useBootstrapClientsData({
    companyId: companySlug && companySlug !== 'undefined' ? companySlug : undefined,
    enabled: !!companySlug && showChrome && companySlug !== 'undefined' && !!client && companySlug !== 'superadmin' && client?.role !== 'vendor',
  });

  // Bootstrap prefetch: Prefetch Leads data as soon as authenticated app mounts
  // Skip for vendor role (no access to leads → 403)
  useBootstrapLeadsData({
    companyId: companySlug && companySlug !== 'undefined' ? companySlug : undefined,
    enabled: !!companySlug && showChrome && companySlug !== 'undefined' && !!client && companySlug !== 'superadmin' && client?.role !== 'vendor',
  });

  // Bootstrap prefetch: Prefetch Agents data as soon as authenticated app mounts
  // Skip for vendor role (no access to agents → 403)
  useBootstrapAgentsData({
    companyId: companySlug && companySlug !== 'undefined' ? companySlug : undefined,
    enabled: !!companySlug && showChrome && companySlug !== 'undefined' && !!client && companySlug !== 'superadmin' && client?.role !== 'vendor',
  });

  // Bootstrap prefetch: Prefetch Home data as soon as authenticated app mounts
  // This ensures Home page appears instantly when user navigates to it
  // Runs in Layout (not just Home) so it starts immediately after login
  useBootstrapHomeData({
    companyId: companySlug && companySlug !== 'undefined' ? companySlug : undefined,
    enabled: !!companySlug && showChrome && companySlug !== 'undefined' && !!client && companySlug !== 'superadmin',
  });

  // Bootstrap prefetch: Prefetch Inventory data as soon as authenticated app mounts
  // This ensures Inventory page appears instantly when user clicks on it
  // Runs in Layout (not just Home) so it starts immediately after login
  useBootstrapInventoryData({
    companyId: companySlug && companySlug !== 'undefined' ? companySlug : undefined,
    enabled: !!companySlug && showChrome && companySlug !== 'undefined' && !!client && companySlug !== 'superadmin',
  });

  // Post-login bootstrap: Prefetch Clients, Leads, Agents in parallel
  // Skip for vendor (no access to those APIs → 403)
  usePostLoginBootstrap({
    companySlug: companySlug && companySlug !== 'undefined' ? companySlug : undefined,
    enabled: !!companySlug && showChrome && companySlug !== 'undefined' && !!client && companySlug !== 'superadmin' && client?.role !== 'vendor',
  });

  // NO GLOBAL LOADING: Bootstrap data loads in background, UI renders immediately
  // All pages (including Clients) render instantly without waiting for bootstrap

  // === Desktop sidebar width (synced with Sidebar collapse state) ===
  // Sidebar stores collapse flag in localStorage under 'sidebar:collapsed'.
  const [sidebarWidth, setSidebarWidth] = useState<'16rem' | '4.5rem'>(() =>
    localStorage.getItem('sidebar:collapsed') === '1' ? '4.5rem' : '16rem'
  );

  // Update margin when the collapse flag changes (same tab)
  useEffect(() => {
    const onResize = () => {
      const collapsed = localStorage.getItem('sidebar:collapsed') === '1';
      setSidebarWidth(collapsed ? '4.5rem' : '16rem');
    };
    // Sidebar can optionally dispatch this event after toggling.
    window.addEventListener('sidebar:width', onResize as EventListener);
    // Also refresh on focus (covers most cases even without custom event)
    window.addEventListener('focus', onResize);
    return () => {
      window.removeEventListener('sidebar:width', onResize as EventListener);
      window.removeEventListener('focus', onResize);
    };
  }, []);

  // Prevent body scroll when the mobile drawer is open (using unified scroll lock)
  const isMobile = useMediaQuery('(max-width: 767px)');
  useBodyScrollLockAuto(sidebarOpen && isMobile);

  // Desktop content offset (no offset on mobile or on /login)
  const contentStyle = useMemo<React.CSSProperties>(
    () => ({
      marginRight: showChrome && isDesktop ? sidebarWidth : undefined,
      paddingRight: showChrome && isDesktop ? '1rem' : undefined, // Gap between content and sidebar
    }),
    [showChrome, isDesktop, sidebarWidth]
  );

  // NO GLOBAL LOADING: All pages render immediately, data fills in when ready
  // This provides instant UI experience like big CRMs (HubSpot, Salesforce, Monday)
  // Pages handle their own loading states internally without blocking the entire UI

  // Show error state if bootstrap fails (but don't block the app)
  if (bootstrapError && showChrome) {
    console.warn('[Layout] Bootstrap error (non-critical):', bootstrapError);
    // Continue rendering - components will handle missing data gracefully
  }

  return (
    <div
      className={`
        flex md:flex-row-reverse w-full overflow-x-hidden
        bg-gradient-to-br from-gray-100 via-white to-gray-100
        antialiased text-gray-900
        ${sidebarOpen ? 'touch-none' : ''}
        relative
      `}
      style={{ 
        height: '100dvh',
        maxHeight: '100dvh',
        paddingBottom: 'env(safe-area-inset-bottom)' 
      }}
    >
      {showChrome && <Sidebar isOpen={sidebarOpen} onClose={closeSidebar} />}

      {/* CONTENT COLUMN — the ONLY scroll container. Explicit height + touch-action fix Chrome wheel/scroll in main area. */}
      <div
        className={`
          flex-1 flex flex-col min-w-0 min-h-0
          transition-[margin] duration-300
          overflow-y-auto overscroll-y-contain
        `}
        style={{ 
          WebkitOverflowScrolling: 'touch',
          touchAction: 'pan-y',
          ...contentStyle 
        }}
      >
        {/* Fixed header - rendered outside scrolling column */}
        {showChrome && <Header onToggleSidebar={toggleSidebar} />}
        
        {/* Trial Banner - shows when account is in trial (fixed below header) */}
        {showChrome && <TrialBanner />}
        
        {/* Storage Full Banner - shows when storage is full or almost full (fixed below header) */}
        {showChrome && <StorageFullBanner />}
        
        {/* Spacer for fixed header and banner - exact height, no gap */}
        {showChrome && <BannerSpacer isDesktop={isDesktop} />}

        <main
          className={`
            flex-shrink-0 min-w-0
            overflow-visible
          `}
          style={{ 
            paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1.5rem)',
            paddingTop: '1rem' // Top padding to ensure content doesn't get covered and has breathing room
          }}
        >
          {/* Single container with all content - relative positioning to prevent stacking during transitions */}
          <div className="relative bg-transparent sm:bg-white sm:rounded-xl sm:shadow-sm md:shadow-md px-3 sm:px-4 md:px-6 py-4 sm:py-5 md:py-6 max-w-[1600px] mx-auto">
            {/* Announcements Wall - shows for all authenticated users */}
            {showChrome && <AnnouncementsWall />}
            {/* Badge Upgrade Popup - shows when badge is upgraded */}
            {showChrome && <BadgeUpgradePopup />}
            {/* Company Badge Display - shows badge on all pages */}
            {/* {showChrome && <CompanyBadgeDisplay position="top-right" />} */}
            {/* Outlet renders the current route - React Router handles transitions */}
            {/* Using location.key ensures proper cleanup on route changes */}
            <div key={location.pathname} className="relative">
              <Outlet />
            </div>
          </div>
        </main>

        <footer
          className={`
            mt-auto
            bg-white/95 backdrop-blur-sm border-t border-gray-200/60
            px-3 sm:px-4 py-3 sm:py-4
            text-xs sm:text-sm text-gray-600
            shadow-sm
          `}
        >
          <div className="max-w-7xl mx-auto w-full flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4">
            <div className="flex items-center gap-1">
              <span>גרסה:</span>
              <span className="font-semibold text-blue-600">{version}</span>
            </div>
            <div className="hidden sm:block text-gray-300">|</div>
            <div className="text-center sm:text-right">
              &copy; {new Date().getFullYear()} | כל הזכויות שמורות{' '}
              <a
                href="http://wjsinnovation.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-blue-600 hover:text-blue-700 hover:underline transition-colors"
              >
                WJS Digital Innovations
              </a>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default Layout;
